import { useMemo } from 'react';
import type { Mode } from '@/types/entity-mode';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useModeStore } from '@/state/stores/use-mode-store';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { toFlowUiMode } from './normalize-flow-plan-view';
import { debugFlow } from './flow-detail.utils';

export type UseFlowModeSwitchingOptions = {
  flowId?: string;
  flow?: FlowDto | null;
};

export type UseFlowModeSwitchingReturn = {
  currentMode: Mode;
  hasPlanMode: boolean;
  hasOrganizeMode: boolean;
  handleModeChange: (newMode: Mode) => void;
  flowToUse: FlowDto | null;
};

/**
 * Hook for managing flow mode switching logic.
 * Handles mode state, availability checks, and mode changes.
 */
export const useFlowModeSwitching = ({
  flowId: flowIdOverride,
  flow: flowOverride,
}: UseFlowModeSwitchingOptions = {}): UseFlowModeSwitchingReturn => {
  const flowStore = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const flowMode = useModeStore((s) => s.flowMode);
  const setMode = useModeStore((s) => s.setMode);
  const setFlow = useFlowStore((s) => s.setFlow);

  // Determine which flow to use (prefer override, then store)
  const flowToUse = flowOverride ?? flowStore ?? null;

  // Get current flow ID for mode switching
  const flowId = useMemo(() => {
    const activeFlow = flowOverride ?? flowStore;
    if (activeFlow?.origin) {
      return typeof activeFlow.origin === 'string' ? activeFlow.origin : activeFlow.origin.id;
    }
    return activeFlow?.id ?? flowIdOverride;
  }, [flowOverride, flowStore, flowIdOverride]);

  // Get current mode from flowMode store, but only if the data for that mode exists
  const currentMode: Mode = useMemo(() => {
    const requestedMode = flowId ? flowMode[flowId] : undefined;
    const hasPlan = !!flowPlan;
    const hasOrganize = !!flowOrganize;
    const flowStoreMode = flowStore?.mode;

    let result: Mode;
    if (flowId && requestedMode) {
      if (requestedMode === 'PLAN' && hasPlan) {
        result = requestedMode;
      } else if ((requestedMode === 'ORGANIZE' || requestedMode === 'PERFORM') && hasOrganize) {
        result = requestedMode;
      } else {
        // Requested mode not available, fall through to defaults
        if (hasPlan) {
          result = 'PLAN';
        } else if (hasOrganize) {
          result = 'ORGANIZE';
        } else {
          result = toFlowUiMode(flowStoreMode);
        }
      }
    } else {
      // Default: PLAN if available, else ORGANIZE
      if (hasPlan) {
        result = 'PLAN';
      } else if (hasOrganize) {
        result = 'ORGANIZE';
      } else {
        result = toFlowUiMode(flowStoreMode);
      }
    }

    debugFlow('[useFlowModeSwitching] currentMode calculation', {
      flowId,
      requestedMode,
      hasPlan,
      hasOrganize,
      flowStoreMode,
      result,
      flowModeStore: flowMode,
    });

    return result;
  }, [flowId, flowMode, flowPlan, flowOrganize, flowStore]);

  // Check if modes are available
  const hasPlanMode = !!flowPlan;
  const hasOrganizeMode = !!flowOrganize;

  // Handle mode change
  const handleModeChange = (newMode: Mode) => {
    if (!flowId) {
      return;
    }
    if (newMode === currentMode) {
      return;
    }
    if (flowId && newMode !== currentMode) {
      // Validate flow mode availability
      if (newMode === 'PLAN' && !flowPlan) {
        return;
      }
      if ((newMode === 'ORGANIZE' || newMode === 'PERFORM') && !flowOrganize) {
        return;
      }

      // Update flowMode store
      const currentFlowMode = useModeStore.getState().flowMode;
      const updatedFlowMode = {
        ...currentFlowMode,
        [flowId]: newMode,
      };

      setMode({
        flowMode: updatedFlowMode,
      });

      // Update computed flow based on selected mode
      if (newMode === 'PLAN' && flowPlan) {
        setFlow(flowPlan);
      } else if (newMode === 'ORGANIZE' && flowOrganize) {
        setFlow(flowOrganize);
      }
    }
  };

  return {
    currentMode,
    hasPlanMode,
    hasOrganizeMode,
    handleModeChange,
    flowToUse,
  };
};
